/**
 * AssetBadgeDropdown - Dropdown zum Wechseln des Assets im Badge.
 *
 * Zeigt alle verfügbaren Brand-Assets zum Austausch.
 */
import React, { useEffect, useRef } from 'react';

export interface BrandAssetOption {
  assetId: string;
  assetName: string;
  assetType: 'logo' | 'style_image' | 'reference_image';
  thumbnailUrl: string;
}

interface AssetBadgeDropdownProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableAssets: BrandAssetOption[];
  currentAssetId: string;
  onAssetSelect: (asset: BrandAssetOption) => void;
}

export const AssetBadgeDropdown: React.FC<AssetBadgeDropdownProps> = ({
  open,
  onOpenChange,
  availableAssets,
  currentAssetId,
  onAssetSelect,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Click outside handler
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onOpenChange(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div
      ref={dropdownRef}
      className="w-72 p-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-lg"
    >
        <div className="space-y-1">
          <div className="px-2 py-1.5 text-xs font-medium text-neutral-500">
            Asset wechseln
          </div>
          {availableAssets.map((asset) => {
            const isActive = asset.assetId === currentAssetId;

            return (
              <button
                key={asset.assetId}
                onClick={() => {
                  onAssetSelect(asset);
                  onOpenChange(false);
                }}
                className={`
                  w-full flex items-center gap-3 p-2 rounded-lg transition-colors text-left
                  ${isActive
                    ? 'bg-primary-50 dark:bg-primary-900/20'
                    : 'hover:bg-neutral-100 dark:hover:bg-neutral-800'
                  }
                `}
              >
                <img
                  src={asset.thumbnailUrl}
                  className="w-10 h-10 rounded object-cover border border-neutral-200 dark:border-neutral-700"
                  alt={asset.assetName}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-neutral-900 dark:text-neutral-100 truncate">
                    {asset.assetName}
                  </div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400">
                    {asset.assetType === 'logo' ? 'Logo' : asset.assetType === 'reference_image' ? 'Referenzbild' : 'Style Reference'}
                  </div>
                </div>
                {isActive && (
                  <svg
                    className="w-5 h-5 text-primary-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      </div>
  );
};
